/**
 * Fixed-timestep accumulator.
 *
 * Simulation runs at a constant rate regardless of display refresh, so physics
 * and combat timing behave identically on a 60Hz phone and a 120Hz tablet.
 * Rendering interpolates between the last two simulation states using `alpha`,
 * which keeps motion smooth when the two rates disagree.
 */

export const DEFAULT_STEP_MS = 1000 / 60;

/**
 * Ceiling on how much time one frame may contribute. Without it, a long stall
 * (backgrounded tab, GC pause, breakpoint) queues up hundreds of steps that
 * take longer to simulate than the stall itself — the classic spiral of death.
 */
export const DEFAULT_MAX_FRAME_MS = 250;

export interface FixedTimestepOptions {
  readonly stepMs?: number;
  readonly maxFrameMs?: number;
}

export class FixedTimestep {
  readonly stepMs: number;
  readonly maxFrameMs: number;

  private accumulator = 0;

  constructor(options: FixedTimestepOptions = {}) {
    this.stepMs = options.stepMs ?? DEFAULT_STEP_MS;
    this.maxFrameMs = options.maxFrameMs ?? DEFAULT_MAX_FRAME_MS;
  }

  /**
   * Feed elapsed wall-clock time; returns how many simulation steps to run.
   * Negative or non-finite deltas (clock adjustments, tab restore) are ignored.
   */
  advance(frameMs: number): number {
    if (!Number.isFinite(frameMs) || frameMs <= 0) return 0;

    this.accumulator += Math.min(frameMs, this.maxFrameMs);

    let steps = 0;
    while (this.accumulator >= this.stepMs) {
      this.accumulator -= this.stepMs;
      steps++;
    }
    return steps;
  }

  /** Fraction of the way into the next step, for render interpolation. */
  get alpha(): number {
    return this.accumulator / this.stepMs;
  }

  /** Seconds per step, the unit simulation systems integrate against. */
  get stepSeconds(): number {
    return this.stepMs / 1000;
  }

  reset(): void {
    this.accumulator = 0;
  }
}

/**
 * Linear interpolation between the previous and current simulation values.
 * Rendering always draws a slightly stale state; that is the correct trade for
 * smooth motion.
 */
export function interpolate(previous: number, current: number, alpha: number): number {
  return previous + (current - previous) * alpha;
}
